import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, MenuItem, Typography, Autocomplete } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarUbicacionesPPE = () => {
  const navigate = useNavigate()
  const { state } = useLocation() // Recibimos la fila seleccionada desde la grilla
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Rescatamos el código original por si el usuario decide cambiar el código principal
  const ubicodigoViejo = state?.ubicodigo ?? ""

  // Estado del formulario
  const [formData, setFormData] = useState({
    ubicodigo: "",
    ubidescri: "",
    ubistatus: "A",
    loccodigo: "",
  })

  useEffect(() => {
    // Precargar los inputs con los valores de la base de datos
    if (state) {
      setFormData({
        ubicodigo: state.ubicodigo || "",
        ubidescri: state.ubidescri || "",
        ubistatus: state.ubistatus || "A",
        loccodigo: state.loccodigo || "",
      })
    }
  }, [state])

  // --- LÓGICA DEFENSIVA PARA LOCALIDADES ---
  const { data: rawLocalidades, isLoading: isLoadingLoc } = useQuery({
    queryKey: ["listaLocalidadesPPE"],
    queryFn: async () => {
      try {
        const response = await api.get("/UbicacionesPPE/getListaLocalidades")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        console.error("Error al cargar Localidades:", error)
        return []
      }
    },
  })

  const locValidadas = Array.isArray(rawLocalidades)
    ? rawLocalidades
    : Array.isArray(rawLocalidades?.data)
      ? rawLocalidades.data
      : []
  const listaLocalidades = locValidadas.map((item) => ({
    id: item.loccodigo || item.id,
    label: item.label || `${item.loccodigo} - ${item.locdescri || ""}`,
  }))
  // --- FIN LÓGICA DEFENSIVA ---

  const {
    mutateAsync: SaveEdicionUbicacion,
    isPending: isSavingEdicionUbicacion,
    isError,
  } = useMutation({
    queryKey: ["isEditingUbicacion"],
    fn: async (data) => {
      const response = await api.post("/UbicacionesPPE/updateUbicacionesPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  // Manejar cambios en los campos del formulario
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "ubistatus" ? value : value.toUpperCase(),
    }))
  }

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Preparar payload tal cual lo espera el backend para la edición
    const payload = {
      ubicodigoNew: formData.ubicodigo,
      ubicodigoOld: ubicodigoViejo,
      ubidescri: formData.ubidescri,
      ubistatus: formData.ubistatus,
      loccodigo: formData.loccodigo,
    }

    try {
      if (!payload.loccodigo.trim()) return showWarning("Debe seleccionar una Localidad")
      if (!payload.ubicodigoNew.trim()) return showWarning("El Código de Ubicación es obligatorio")
      if (!payload.ubidescri.trim()) return showWarning("La Descripción de la Ubicación es obligatoria")

      await SaveEdicionUbicacion(payload)
    } catch (error) {
      console.error("Error al editar la Ubicación:", error)
      if (error?.code === "VALIDATION_ERROR") {
        showWarning(error.message)
      }
    }
  }

  // Acción dinámica de la barra de menú
  const ejecutarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "EJECUTAR")
  const toolbarActions = ejecutarAction
    ? [
        {
          label: ejecutarAction.acccaption,
          key: ejecutarAction.acccaption,
          icon: getIconComponent(ejecutarAction.accnameicono, ejecutarAction.acctipoico),
        },
      ]
    : []

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* Toolbar superior con el botón de Guardar (Ejecutar) */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicionUbicacion || isLoadingLoc}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Ubicación PPE</b>
        </div>

        <CustomBackdrop isLoading={isSavingEdicionUbicacion || isLoadingLoc} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos de la Ubicación
            </Typography>
            <Grid container spacing={3}>
              {/* COMBO LOCALIDAD */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaLocalidades}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    listaLocalidades.find((c) => c.id === formData.loccodigo) ||
                    (formData.loccodigo ? { id: formData.loccodigo, label: `${formData.loccodigo}` } : null)
                  }
                  onChange={(event, newValue) => handleInputChange("loccodigo", newValue ? newValue.id : "")}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Localidad *"
                      placeholder="Buscar..."
                      InputLabelProps={{ shrink: true }}
                      error={isError && !formData.loccodigo}
                    />
                  )}
                />
              </Grid>

              {/* CÓDIGO */}
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código *"
                  value={formData.ubicodigo}
                  onChange={(e) => handleInputChange("ubicodigo", e.target.value)}
                  error={isError && !formData.ubicodigo}
                  inputProps={{ maxLength: 8 }}
                />
              </Grid>

              {/* DESCRIPCIÓN */}
              <Grid item xs={12} sm={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.ubidescri}
                  onChange={(e) => handleInputChange("ubidescri", e.target.value)}
                  error={isError && !formData.ubidescri}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* ESTADO (ACTIVO/INACTIVO) */}
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.ubistatus}
                  onChange={(e) => handleInputChange("ubistatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarUbicacionesPPE
